import { CaretRightIcon } from "@phosphor-icons/react";
import { Fragment } from "react";
import { Button } from "@/components/ui/button";
import { WorkflowHeader } from "@/components/ui/WorkflowHeader";
import { cn } from "@/lib/utils";
import type { BriefingCreateStep } from "@/store/scheduled-briefings/briefing-create/briefing-create.slice";

const STEP_HEADER_TITLE: Record<BriefingCreateStep, string> = {
  "choose-template": "Choose template",
  configure: "Configure",
};

const CREATE_BRIEFING_STEPS: {
  id: BriefingCreateStep;
  number: number;
  label: string;
}[] = [
  { id: "choose-template", number: 1, label: "Choose template" },
  { id: "configure", number: 2, label: "Configure" },
];

interface BriefingCreateWorkflowStepProps {
  number: number;
  label: string;
  isActive: boolean;
}

const BriefingCreateWorkflowStep = ({
  number,
  label,
  isActive,
}: BriefingCreateWorkflowStepProps) => (
  <li
    aria-current={isActive ? "step" : undefined}
    className={cn(
      "flex h-6 items-center gap-1.5 rounded-full py-0.5 pl-[7px] pr-2",
      isActive ? "bg-violet-50" : "bg-white",
    )}
  >
    <span
      aria-hidden
      className={cn(
        "flex size-4 items-center justify-center rounded-full border text-[8px] font-semibold leading-5 tracking-[0.08px]",
        isActive
          ? "border-violet-700 text-violet-700"
          : "border-gray-400 text-gray-500",
      )}
    >
      {number}
    </span>
    <span
      className={cn(
        "text-xs leading-5 tracking-[0.12px] whitespace-nowrap",
        isActive ? "font-medium text-violet-700" : "font-normal text-gray-500",
      )}
    >
      {label}
    </span>
  </li>
);

export interface BriefingCreateWorkflowHeaderProps {
  step: BriefingCreateStep;
  /** Editing a saved briefing: no template step, so the progress indicator is hidden. */
  isEditing?: boolean;
  /** Saved briefing the user can't edit (`canEdit: false`). */
  isReadOnly?: boolean;
  isSaving?: boolean;
  isSaveDisabled?: boolean;
  onSave?: () => void;
  onClose: () => void;
}

export const BriefingCreateWorkflowHeader = ({
  step,
  isEditing = false,
  isReadOnly = false,
  isSaving = false,
  isSaveDisabled = false,
  onSave,
  onClose,
}: BriefingCreateWorkflowHeaderProps) => (
  <WorkflowHeader
    title={
      isReadOnly
        ? "View briefing"
        : isEditing
          ? "Edit briefing"
          : STEP_HEADER_TITLE[step]
    }
    onClose={onClose}
    // Closing mid-save drops the response while the server still commits it.
    isCloseDisabled={isSaving}
    center={
      isEditing ? null : (
        <nav aria-label="Create briefing progress">
          <ol className="flex items-center gap-2">
            {CREATE_BRIEFING_STEPS.map((createStep, index) => (
              <Fragment key={createStep.id}>
                <BriefingCreateWorkflowStep
                  number={createStep.number}
                  label={createStep.label}
                  isActive={step === createStep.id}
                />
                {index < CREATE_BRIEFING_STEPS.length - 1 ? (
                  <li aria-hidden className="list-none">
                    <CaretRightIcon className="size-3.5 text-gray-400" />
                  </li>
                ) : null}
              </Fragment>
            ))}
          </ol>
        </nav>
      )
    }
    actions={
      onSave ? (
        <Button
          type="button"
          variant="default"
          size="sm"
          className="h-7 min-w-0 shrink-0 px-2.5 text-xs font-medium"
          isDisabled={isSaving || isSaveDisabled}
          onPress={onSave}
        >
          {isSaving
            ? "Saving…"
            : isEditing
              ? "Save changes"
              : "Create briefing"}
        </Button>
      ) : null
    }
  />
);
